import { useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Info, MessageCircle, Printer } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { CURRENT_CLIENT_ID } from "@/data/constants";
import { Avatar, Card, CardTitle, Segmented, StatusBadge, btn, avatarUrl } from "@/components/common";
import { FactList, InvoiceTotals, InvoicePanel, ItineraryTimeline, PrintableItinerary, QuoteTerms } from "@/components/TripSections";
import { AuthorizePayment, AuthorizationsPanel } from "@/components/PaymentAuthorizations";
import { TripDocuments } from "@/components/TripDocuments";
import { daysUntil, fmtDate, fmtShort, itinerary, returnDate } from "@/lib/trip";

function ApproveQuote({ trip }) {
  const { convert, send } = useDemo();
  const [confirm, setConfirm] = useState(false);
  const nav = useNavigate();
  const approve = () => {
    if (!confirm) return setConfirm(true);
    convert(trip.id, true);
    send(trip.clientId, "client", `I've approved quote ${trip.quoteNumber} for ${trip.title}. Thank you!`, trip.id);
    toast.success("Quote approved", { description: `${trip.title} is now in your upcoming trips.` });
    nav("/mytrip/trips");
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <button onClick={approve} data-testid="client-approve-quote-btn" className={`${btn.primary} h-9 ${confirm ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}><CheckCircle2 className="h-4 w-4" />{confirm ? "Tap to confirm approval" : "Approve Quote"}</button>
      {confirm ? <button onClick={() => setConfirm(false)} data-testid="client-approve-cancel-btn" className={`${btn.secondary} h-9`}>Cancel</button>
        : <Link to="/mytrip/messages" data-testid="client-quote-ask-btn" className={`${btn.secondary} h-9`}><MessageCircle className="h-4 w-4" />Ask a Question</Link>}
    </div>
  );
}

function Overview({ trip }) {
  const { paidExtra, settings } = useDemo();
  const extra = paidExtra(trip.id);
  return (
    <div className="space-y-4">
      {trip.status === "QUOTE" && (
        <Card className="border-amber-200 bg-amber-50/60 p-4" data-testid="client-quote-notice">
          <div className="flex items-center gap-3 border-b border-amber-200/70 pb-3">
            <Avatar name={settings.advisorName} src={avatarUrl(settings.advisorId, settings.advisorAvatar)} size="md" data-testid="quote-advisor-photo" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Prepared by your advisor</p>
              <p className="truncate text-sm font-semibold text-navy-900">{settings.advisorName}</p>
            </div>
          </div>
          <p className="mt-3 text-sm font-semibold text-navy-900">Quote {trip.quoteNumber}</p>
          <p className="mt-1 text-xs text-slate-600">Review the details and payments below. Approving confirms the trip and moves it to your upcoming trips.</p>
          <ApproveQuote trip={trip} />
        </Card>
      )}
      <Card><CardTitle>Trip Details</CardTitle><div className="p-5"><FactList trip={trip} compact /></div></Card>
      <Card><CardTitle>Travelers</CardTitle>
        <ul className="space-y-3 p-5">{(trip.travelers || []).map((n) => <li key={n} className="flex items-center gap-3"><Avatar name={n} size="sm" /><span className="text-sm font-medium text-navy-900">{n}</span></li>)}</ul>
      </Card>
      <Card><CardTitle>Invoice Summary</CardTitle><div className="p-5"><InvoiceTotals trip={trip} extraPaid={extra} /></div></Card>
      {trip.status === "QUOTE" && <Card className="overflow-hidden p-0" data-testid="client-quote-terms"><QuoteTerms /></Card>}
    </div>
  );
}

function Payments({ trip }) {
  const { authorizations = [] } = useDemo();
  const auths = authorizations.filter((a) => a.tripId === trip.id);
  const extraPaid = auths.filter((a) => a.status === "COMPLETE").reduce((s, a) => s + a.amount, 0);
  return (
    <div className="space-y-4" data-testid="client-payments">
      <p className="flex items-start gap-2 rounded-xl bg-slate-100 px-3.5 py-2.5 text-xs text-slate-600"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />Review your charges and authorize payments below. We never store full card numbers — your advisor applies authorized payments in our secure vault.</p>
      <InvoicePanel trip={trip} extraPaid={extraPaid} extra={<>
        {trip.status === "BOOKED" && <AuthorizePayment trip={trip} />}
        <AuthorizationsPanel tripId={trip.id} />
      </>} />
    </div>
  );
}

function Itinerary({ trip }) {
  const { settings } = useDemo();
  const days = itinerary(trip);
  return (
    <div data-testid="client-itinerary">
      <div className="space-y-4">
        {days.length > 0 && (
          <div className="flex justify-end">
            <button onClick={() => window.print()} data-testid="client-print-itinerary-btn" className={`${btn.secondary} h-9`}><Printer className="h-4 w-4" />Print / Save PDF</button>
          </div>
        )}
        <ItineraryTimeline trip={trip} />
      </div>
      <PrintableItinerary trip={trip} settings={settings} />
    </div>
  );
}

export default function ClientTripDetail() {
  const { id } = useParams();
  const { trip: get } = useDemo();
  const [params, setParams] = useSearchParams();
  const trip = get(id);
  if (!trip || trip.clientId !== CURRENT_CLIENT_ID) return <Navigate to="/mytrip/trips" replace />;
  const tab = params.get("tab") || "overview";
  const d = daysUntil(trip.departure);

  return (
    <div data-testid="client-trip-detail">
      <div className="relative -mx-4 -mt-5 h-60 overflow-hidden sm:mx-0 sm:mt-0 sm:h-72 sm:rounded-3xl">
        <img src={trip.image || "/images/sunset.jpg"} alt={trip.destination} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/25 to-navy-950/30" />
        <Link to="/mytrip/trips" data-testid="client-back-to-trips" aria-label="Back to trips" className="absolute left-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-navy-900 backdrop-blur"><ArrowLeft className="h-5 w-5" /></Link>
        <StatusBadge status={trip.status} className="absolute right-4 top-6 bg-white" />
        <div className="absolute bottom-5 left-5 right-5 text-white">
          <p className="text-sm text-white/75">{trip.destination}</p>
          <h1 className="font-display text-2xl font-bold leading-tight sm:text-3xl" data-testid="client-trip-title">{trip.title}</h1>
          <div className="mt-2 flex items-end justify-between gap-3">
            <p className="text-sm text-white/80">{trip.vessel}<br />{fmtShort(trip.departure)} – {fmtDate(returnDate(trip))}</p>
            {trip.status === "BOOKED" && d >= 0 && <p className="shrink-0 text-right"><span className="font-display text-2xl font-bold">{d}</span><span className="block text-[10px] uppercase tracking-widest text-white/60">days to go</span></p>}
          </div>
        </div>
      </div>
      <div className="my-5">
        <Segmented testPrefix="client-trip-tab" value={tab} onChange={(v) => setParams({ tab: v }, { replace: true })} options={[["overview", "Overview"], ["itinerary", "Itinerary"], ["documents", "Documents"], ["payments", "Payments"]].map(([v, l]) => ({ value: v, label: l }))} />
      </div>
      {tab === "overview" && <Overview trip={trip} />}
      {tab === "itinerary" && <Itinerary trip={trip} />}
      {tab === "documents" && <TripDocuments trip={trip} />}
      {tab === "payments" && <Payments trip={trip} />}
    </div>
  );
}
